/**
 * Pure helpers for the client bootstrap, kept out of `main.tsx` so the branch logic is unit-testable: importing
 * `main.tsx` pulls in the tRPC client / persister / server-fn, which is not node-safe.
 */

export interface TestProgressUser {
  testPassed?: boolean | null
  testPassedValues?: boolean | null
  testPassedPersonality?: boolean | null
}

/** The first unfinished questionnaire, so a TEST_INCOMPLETE redirect resumes where the user left off. */
export type TestPath = '/test/competences' | '/test/values' | '/test/personality'

export function pickIncompleteTestPath(user: TestProgressUser | null | undefined): TestPath {
  if (!user?.testPassed) return '/test/competences'
  if (!user.testPassedValues) return '/test/values'
  return '/test/personality'
}

/**
 * Whether a failed query reaches the global error handler (toast or redirect). Not without an observer: a hover
 * preload's error would surface "from nowhere". Nor a `quietRefetch` query's refresh of data still on screen: the
 * data stays, so telling the reader it is lost would be false; its first load still surfaces.
 */
export function surfacesQueryError(query: {
  getObserversCount: () => number
  state: { data: unknown }
  meta?: { quietRefetch?: true } | undefined
}): boolean {
  if (query.getObserversCount() === 0) return false
  return !(query.meta?.quietRefetch && query.state.data !== undefined)
}

/**
 * What the client `toastErrorLink` should do for a tRPC error, from its machine `code`, the transport `dataCode`
 * and whether a Lingui translation exists. `main.tsx` owns the side effects.
 *  - 'redirect-login'    a 401 with no live session, except INVALID_CREDENTIALS (which must show in place).
 *  - 'redirect-test'     the applicant has not finished the test → resume the questionnaire.
 *  - 'redirect-report'   an applicant reached a company-side section the nav hides from them → own profile.
 *  - 'redirect-company'  an employee in company view requested their own personal report → company home.
 */
export type TrpcErrorAction =
  | 'redirect-login'
  | 'redirect-test'
  | 'redirect-report'
  | 'redirect-company'
  | 'toast-generic'
  | 'toast-translated'
  | 'toast-raw'

/**
 * WHICH CONTEXT the persisted query cache belongs to — see PERSISTED_IDENTITY_KEY in @/main.
 *
 * A tRPC procedure may resolve its scope from the SESSION (`interfaceMode`, the active employer, the
 * employee role), so one query key can hold a different payload per context. The cache keys by key alone,
 * so the guard purges whenever this string changes. Every session field a procedure branches on has to be
 * in here: `main-helpers.unit.test.ts` fails the build if a new one appears server-side unclassified.
 */
export function persistedCacheIdentity(
  user:
    | {
        id: number
        activeCompanyId?: number | null
        interfaceMode?: 'employee' | 'company' | null
        activeEmployer?: { employeeType?: 'administrator' | 'authorized' | 'collaborator' | null } | null
      }
    | null
    | undefined,
): string {
  if (user?.id == null) return 'anon'
  // The role rides along because `activeEmployer` is resolved per REQUEST, so a promotion takes effect
  // without a new session — and it changes what the directory reads return.
  return [
    user.id,
    user.activeCompanyId ?? '',
    user.interfaceMode ?? 'employee',
    user.activeEmployer?.employeeType ?? '',
  ].join(':')
}

export function classifyTrpcErrorToast(input: {
  code: string
  dataCode: string | undefined
  hasTranslation: boolean
}): TrpcErrorAction {
  if (input.dataCode === 'UNAUTHORIZED' && input.code !== 'INVALID_CREDENTIALS') return 'redirect-login'
  if (input.code === 'TEST_INCOMPLETE') return 'redirect-test'
  if (input.code === 'COMPANY_OR_EMPLOYEE_ONLY' || input.code === 'COMPANY_ONLY') return 'redirect-report'
  if (input.code === 'EMPLOYEE_VIEW_ONLY') return 'redirect-company'
  if (input.dataCode === 'INTERNAL_SERVER_ERROR') return 'toast-generic'
  if (input.hasTranslation) return 'toast-translated'
  // A BAD_REQUEST that matched no known code is a Zod failure whose `message` is the stringified issues array.
  if (input.dataCode === 'BAD_REQUEST') return 'toast-generic'
  return 'toast-raw'
}
